import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import {
  Plus, Loader2, CreditCard, ExternalLink, AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

const STATUS_BADGES = {
  DRAFT: 'bg-slate-100 text-slate-700 border-slate-200',
  AUTHORISED: 'bg-blue-100 text-blue-700 border-blue-200',
  PAID: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  VOIDED: 'bg-rose-100 text-rose-700 border-rose-200',
};

const fmtGBP = (n) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(Number(n || 0));
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : '—');
const currentPeriod = () => new Date().toLocaleString('en-GB', { month: 'short', year: 'numeric' });

export const HrPayrollTab = ({ employeeId, employee, isHrAdmin }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('bill');
  const [defaultAmount, setDefaultAmount] = useState(0);
  const [customAmount, setCustomAmount] = useState('');
  const [periodLabel, setPeriodLabel] = useState(currentPeriod());
  const [description, setDescription] = useState('');
  const [invoiceId, setInvoiceId] = useState('');
  const [bankAccountId, setBankAccountId] = useState('');
  const [saving, setSaving] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/hr/employees/${employeeId}/payslips`);
      setRows(r.data || []);
    } catch (e) {
      toast.error('Could not load payslips');
    } finally { setLoading(false); }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line
  }, [employeeId]);

  useEffect(() => {
    const annual = Number(employee?.salary_annual || 0);
    setDefaultAmount(annual ? Math.round((annual / 12) * 100) / 100 : 0);
  }, [employee?.salary_annual]);

  const amountToUse = () => {
    const c = parseFloat(customAmount);
    if (!isNaN(c) && c > 0) return c;
    return defaultAmount;
  };

  const submit = async () => {
    const amount = amountToUse();
    if (!amount || amount <= 0) {
      toast.error('Amount must be > 0. Set salary_annual or enter a custom amount.');
      return;
    }
    if (mode === 'payment' && (!invoiceId.trim() || !bankAccountId.trim())) {
      toast.error('Invoice ID and Bank Account ID required for payment mode');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        mode,
        amount: parseFloat(customAmount) || undefined,
        period_label: periodLabel,
        description: description || undefined,
      };
      if (mode === 'payment') {
        payload.invoice_id = invoiceId.trim();
        payload.bank_account_id = bankAccountId.trim();
      }
      const r = await api.post(`/hr/employees/${employeeId}/payslips`, payload);
      toast.success(mode === 'bill'
        ? `Draft Bill ${r.data.xero_invoice_number || ''} created in Xero`
        : 'Payment recorded in Xero');
      setOpen(false);
      setCustomAmount(''); setInvoiceId(''); setBankAccountId(''); setDescription('');
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Xero call failed');
    } finally { setSaving(false); }
  };

  return (
    <Card className="border-slate-200" data-testid="hr-payroll-tab">
      <CardHeader className="pb-3 flex items-center justify-between">
        <CardTitle className="text-base flex items-center gap-2">
          <CreditCard className="w-4 h-4 text-slate-500" /> Payroll &amp; Payslips
        </CardTitle>
        {isHrAdmin && (
          <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => setOpen(true)} data-testid="payroll-new-btn">
            <Plus className="w-4 h-4 mr-1" /> Pay Employee
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="flex items-start gap-2 p-2.5 rounded bg-blue-50 border border-blue-200 text-xs text-blue-900">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>Default monthly amount = annual salary ÷ 12. Bills post as DRAFT in Xero so you can review before authorising.</span>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
        ) : rows.length === 0 ? (
          <p className="text-center text-slate-400 py-10 text-sm">No payslips recorded yet.</p>
        ) : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Period</TableHead><TableHead>Mode</TableHead><TableHead>Amount</TableHead>
              <TableHead>Xero Ref</TableHead><TableHead>Status</TableHead><TableHead>Recorded</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.map(r => (
                <TableRow key={r.id} data-testid={`payslip-row-${r.id}`}>
                  <TableCell className="text-sm font-medium">{r.period_label}</TableCell>
                  <TableCell><Badge variant="outline" className="capitalize text-[10px]">{r.mode}</Badge></TableCell>
                  <TableCell className="text-sm tabular-nums">{fmtGBP(r.amount)}</TableCell>
                  <TableCell className="text-sm font-mono text-[11px]">{r.xero_invoice_number || (r.xero_invoice_id || '').slice(0, 8) || '—'}</TableCell>
                  <TableCell>
                    <Badge className={`${STATUS_BADGES[r.status] || 'bg-slate-100 text-slate-700'} border text-[10px]`}>
                      {r.status || 'DRAFT'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs text-slate-500">{fmtDate(r.created_at)}<br /><span className="text-[10px]">by {r.created_by}</span></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="payroll-dialog">
          <DialogHeader>
            <DialogTitle>Pay {employee?.first_name} {employee?.last_name}</DialogTitle>
            <DialogDescription>
              Bills post as DRAFT in Xero. Payments record against an existing Xero Bill.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Mode</Label>
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger data-testid="payroll-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="bill">Create Xero Bill (DRAFT)</SelectItem>
                  <SelectItem value="payment">Record Payment against existing Bill</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Period</Label>
              <Input value={periodLabel} onChange={(e) => setPeriodLabel(e.target.value)} placeholder="e.g. May 2026" data-testid="payroll-period" />
            </div>
            <div className="grid grid-cols-2 gap-3 items-end">
              <div>
                <Label className="text-xs">Default ({fmtGBP(defaultAmount)})</Label>
                <p className="text-[11px] text-slate-500 py-2">annual ÷ 12</p>
              </div>
              <div>
                <Label className="text-xs">Custom amount (£)</Label>
                <Input type="number" step="0.01" value={customAmount} onChange={(e) => setCustomAmount(e.target.value)} placeholder="Leave blank to use default" data-testid="payroll-custom-amount" />
              </div>
            </div>
            {mode === 'bill' && (
              <div>
                <Label className="text-xs">Line Description (optional)</Label>
                <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder={`Payroll — ${periodLabel}`} data-testid="payroll-desc" />
              </div>
            )}
            {mode === 'payment' && (
              <>
                <div>
                  <Label className="text-xs">Xero Invoice ID</Label>
                  <Input value={invoiceId} onChange={(e) => setInvoiceId(e.target.value)} placeholder="UUID of the existing Bill" data-testid="payroll-invoice-id" />
                  <p className="text-[11px] text-slate-500 mt-1">Find this in Xero → Bills → open bill → URL contains the GUID.</p>
                </div>
                <div>
                  <Label className="text-xs">Bank Account ID</Label>
                  <Input value={bankAccountId} onChange={(e) => setBankAccountId(e.target.value)} placeholder="UUID of the Xero bank account" data-testid="payroll-bank-id" />
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={submit} disabled={saving} data-testid="payroll-save">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <ExternalLink className="w-4 h-4 mr-1" />}
              Push to Xero
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default HrPayrollTab;
